import React from 'react';
import styled from 'styled-components';

import { MQS, COLORS } from '../../app/common/constants/styles';
import Icon from '../../app/common/components/Icon';
import ICONS from '../../app/common/constants/icons';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { content, services } from './Constant';
import ServicesIllustration from '../ServicesIllustration';
import { MobileDeviceCheck } from './index';
import { addRatEvent } from '../../utils/ratClickEvents';

interface IBuBodyText {
    title: string;
    body: string;
    links: Array<Record<string, any>>;
    linkTexts: Array<React.ReactElement>;
    image: Record<string, string>;
}

const Container = styled.div`
    display: none;
    justify-content: space-between;
    margin: 40px 0px;
    flex-direction: row;
    opacity: 1;
    animation: container 4s ease;
    min-height: 355px;
    &.show {
        display: flex;
    }

    @media (max-width: ${MQS.mq2Min}) {
        flex-direction: column;
    }

    @keyframes container {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const LeftContent = styled.div`
    width: 50%;
    height: auto;
    padding: 0px 8% 20px 0px;
    display: flex;
    justify-conetent: flex-start;
    flex-direction: column;

    h2 {
        font-family: 'Rakuten Sans UI Bold', sans-serif;
        line-height: 1.17;
        margin-top: 0px;
    }

    p {
        line-height: 24px;
        font-size: 18px;
    }

    a {
        font-family: Rakuten Sans UI Semi-Bold, sans-serif;
        color: ${COLORS.rakutenRed};
        cursor: pointer;
        align-items: center;
        display: flex;
        line-height: 22px;
        text-decoration: none;
        width: fit-content;
    }

    @media (max-width: ${MQS.mq2Min}) {
        width: 100%;
        padding: 20px 0px 20px 0px;
    }

    span {
        text-align: center;
        position: relative;
        padding-bottom: 4px;
        margin: 2px 0px;
    }

    span::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 2px;
        bottom: 0;
        left: 0;
        background-color: #bf0000;
        visibility: hidden;
        transform: scaleX(0);
        transition: all 0.3s ease-in-out 0s;
    }
    span:hover:before {
        visibility: visible;
        transform: scaleX(1);
    }
`;

const RightContent = styled.div`
    width: 50%;
    display: flex;
    justify-content: flex-end;

    @media (max-width: ${MQS.mq2Min}) {
        width: 100%;
    }
`;

const Image = styled.img`
    height: 320px;
    border-radius: 15px;
    object-fit: cover;
    width: fit-content;

    @media (max-width: ${MQS.mq2Min}) {
        width: 100%;
        height: fit-content;
    }
`;

const RakutenServicesLogo = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: no-wrap;
    width: 100%;
    align-items: flex-end;
    position: relative;
    overflow: scroll;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: ${MQS.mq2Min}) {
        overflow: scroll;
        width: calc(100% - -100px);
        margin: 10px -50px;
        padding: 0px 25px;
    }
`;

const LogoImage = styled.img<{ isBlurred: boolean }>`
    height: 35px;
    opacity: 0;
    opacity: ${(props) => (props.isBlurred ? 1 : 0.5)};
    animation: image 4s ease;
    margin: 0px 20px;
    cursor: pointer;

    &:first-child {
        margin: 0px 20px 0px 0px;
    }

    &:last-child {
        margin: 0px 0px 0px 20px;
    }

    @media (max-width: ${MQS.mq2Min}) {
        width: auto;
        height: 40px;

        &:first-child {
            margin: 0px 20px 0px 25px;
        }

        &:last-child {
            margin: 0px 25px 0px 20px;
        }
    }
`;

const IllustrationWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const BuLinkContent = styled.div`
    display: flex;
    align-items: center;
`;

const BuLink = styled.a``;

const IconContainer = styled.div`
    color: ${COLORS.rakutenRed};
`;

function ServicesContent() {
    const [currentContent, setCurrentContent] = React.useState<string>(services.CROP);
    const [selectedBu, setSelectedBu] = React.useState<string>('');
    const buLinkClick = (linkText: string) => {
        addRatEvent('about', 'go_to_link', {
            element_name: linkText,
        });
    };

    const getBUBodyText = (site: IBuBodyText) => {
        return (
            <React.Fragment>
                <h2>{TranslationHelpers.getFormattedMessage(site.title)}</h2>
                <p>{TranslationHelpers.getFormattedMessage(site.body)}</p>
                {site.links.map((record, idx) => {
                    return (
                        <BuLinkContent key={record.link}>
                            <BuLink
                                href={record.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => buLinkClick(record.link)}
                            >
                                <span>{site.linkTexts[idx]}</span>
                            </BuLink>
                            <IconContainer>
                                <Icon size="24" icon={ICONS.ARROW_RIGHT} />
                            </IconContainer>
                        </BuLinkContent>
                    );
                })}
            </React.Fragment>
        );
    };

    React.useEffect(() => {
        if (currentContent !== selectedBu) {
            setSelectedBu('');
        }
    }, [currentContent, selectedBu]);

    React.useEffect(() => {
        // Auto scroll effect for services logo.
        const parent = document.getElementById(`services-logo`);
        const element = document.getElementById(`img-${currentContent}`);

        if (element && parent) {
            const elParentLeft = parent.offsetLeft + parent.offsetWidth;
            const elLeft = element.offsetLeft + element.offsetWidth;
            parent.style.scrollBehavior = 'smooth';

            // check if element not in view
            if (elLeft >= elParentLeft + parent.scrollLeft) {
                parent.scrollLeft = elLeft - elParentLeft + getScrollingWidth();
            } else if (elLeft <= parent.offsetLeft + parent.scrollLeft) {
                parent.scrollLeft = element.offsetLeft - parent.offsetLeft + -getScrollingWidth();
            } else {
                parent.scrollLeft = elLeft - elParentLeft + getScrollingWidth();
            }
        }
    }, [currentContent]);

    const getScrollingWidth = (): number => {
        return MobileDeviceCheck.any() ? 100 : 1050;
    };

    return (
        <React.Fragment>
            <IllustrationWrapper>
                <ServicesIllustration
                    onChange={(service: string) => setCurrentContent(service)}
                    onBuClick={selectedBu}
                />
            </IllustrationWrapper>
            <RakutenServicesLogo id="services-logo">
                {content.map((element) => {
                    return (
                        <LogoImage
                            key={`${element.id}`}
                            id={`img-${element.id}`}
                            isBlurred={currentContent === element.id}
                            onClick={() => {
                                setSelectedBu(element.id);
                                addRatEvent('about', 'go_to_link', { element_name: element.alt });
                            }}
                            src={`${element.logo.original}`}
                            alt={element.alt}
                            style={element.id === services.FRANCE ? { height: '60px' } : {}}
                        />
                    );
                })}
            </RakutenServicesLogo>
            {content.map((element) => (
                <Container
                    key={element.name}
                    className={element.id === currentContent ? 'show' : undefined}
                >
                    <LeftContent>{getBUBodyText(element)}</LeftContent>
                    <RightContent>
                        <Image
                            src={
                                MobileDeviceCheck.any()
                                    ? element.image.mobile
                                    : element.image.desktop
                            }
                            alt={
                                TranslationHelpers.getMessageDescriptor(element.imageAlt)
                                    .defaultMessage
                            }
                        />
                    </RightContent>
                </Container>
            ))}
        </React.Fragment>
    );
}

export default ServicesContent;
